/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { use, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type { ShopperBasketsV2, ShopperProducts, ShopperPromotions } from '@/scapi';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import type { AppConfig } from '@/types/config';
import { useBasket } from '@/providers/basket';
import { useCurrency } from '@/providers/currency';
import { formatCurrency } from '@/lib/currency';
import { Typography } from '@/components/typography';
import { Button } from '@/components/buttons';
import Checkbox from '@/components/forms/checkbox';
import { Label } from '@/components/forms/label';
import { ToggleCard, ToggleCardEdit, ToggleCardSummary } from '@/components/toggle-card';
import { getDeliveryShipment } from '@/extensions/bopis/lib/basket-utils';
import { isProtectionPlanItem } from '@/lib/extend-basket-utils';
import { useCheckoutActions } from '@/hooks/use-checkout-actions';
import {
    DELIVERY_GROUP_LABEL_KEYS as GROUP_LABEL_KEYS,
    bucketItemsByDeliveryMethod,
    formatDeliveryDate,
    getGroupDeliveryDate,
    getProductItemStringAttr as getStringAttr,
    resolveProductItemImage,
    type DeliveryMethod,
} from '@/lib/checkout/delivery-group-display';
import { getDisplayVariationValues, type EnrichedProductItem } from '@/lib/product/product-utils';
import {
    buildKitGroupByItemId,
    getKitGroupPricing,
    getKitGroups,
    type KitBasketFields,
    type KitGroup,
} from '@/lib/kit-utils';
import ProductPrice from '@/components/product-price';
import CartLinePromoCallouts from '@/components/product-price/cart-line-promo-callouts';
import { DynamicImage } from '@/components/dynamic-image';
import { KitItemVariations } from '@/components/product-kit/kit-item-variations';
import KitGroupImage from '@/components/product-kit/kit-group-image';
import DeliveryMethodToggle, { type DeliveryMethodToggleActions } from './delivery-method-toggle';
import RadioDeliveryMethodTile from './radio-delivery-methods-tile';
import FewerDeliveriesToggle from './fewer-deliveries-toggle';
import { ShippingMethodDetailsModal, type ShippingMethodDetailsContentAssets } from './shipping-method-details-modal';
type ProductItem = ShopperBasketsV2.schemas['ProductItem'];
type DeliveryRow =
    | { kind: 'item'; item: ProductItem }
    | { kind: 'kit'; group: KitGroup; componentItems: ProductItem[] };

/**
 * Minimal slice of `useCheckoutActions` we consume for the Continue-to-Payment
 * button. Defining locally so tests / Storybook can stub without re-typing the
 * full hook return.
 */
interface DeliveryGroupsActions {
    confirmDeliveryOptions: (currentValue: 'HD' | 'THD') => void;
    hdShippingTypeFetcher: { state: 'idle' | 'loading' | 'submitting' };
}

interface DeliveryGroupsProps {
    /** Optional class name for the outer container. */
    className?: string;
    /**
     * Catalog product data keyed by basket item id (the checkout loader's
     * `productMap`). Required to resolve line-item thumbnails — basket
     * `productItems` do not carry image URLs themselves. Resolved within the
     * caller's Suspense boundary; omit in tests/stories that don't assert images.
     */
    productMapPromise?: Promise<Record<string, ShopperProducts.schemas['Product']>>;
    /**
     * Promotions map keyed by promotionId. When provided, the promotion's
     * customer-facing offerMsg/calloutMsg is preferred over the basket-level
     * adjustment text in `CartLinePromoCallouts`. Resolved within the caller's
     * Suspense boundary; omit in tests/stories that don't assert promo copy.
     */
    promotionsPromise?: Promise<Record<string, ShopperPromotions.schemas['Promotion']>>;
    /**
     * Test/Storybook seam: forwards an injected `useActions` to the inline
     * `<DeliveryMethodToggle/>` rendered inside HD line items. The default uses
     * the real `useCheckoutActions` hook (via the toggle's own default).
     */
    toggleUseActions?: () => DeliveryMethodToggleActions;
    /**
     * Test/Storybook seam: lets tests inject a stub for the actions used by
     * the Continue-to-Payment button. Default uses the real
     * `useCheckoutActions` hook.
     */
    useActions?: () => DeliveryGroupsActions;
    /**
     * Step state from the checkout context (mirrors the OOTB
     * <ShippingOptions/> contract). When omitted, the component renders
     * un-collapsed for backwards compatibility with existing stories /
     * tests that pre-date the ToggleCard wrapping.
     */
    isCompleted?: boolean;
    isEditing?: boolean;
    onEdit?: () => void;
    /**
     * Deferred BM content assets for the shipping-method details modal body, keyed by delivery
     * method (`whiteGlove` / `doorstep` / `thirdParty` / `groundShipping`). The modal picks the
     * matching asset based on the active `(variant, methodName)` pair and renders its HTML body.
     * Each asset is fetched independently by the checkout loader and fails open to `null` on
     * 404 / 403 — when an asset is missing the modal renders an empty body for that method only,
     * the others continue to render normally. See `CHECKOUT_DELIVERY_DETAILS_CONTENT_ASSET_IDS`
     * in `./shipping-method-details-modal.tsx`.
     */
    shippingMethodDetailsContentAssets?: ShippingMethodDetailsContentAssets;
}

/** Read a numeric custom attribute off a ProductItem (or basket) without leaking `any`. */
function getNumberAttr(record: Record<string, unknown>, key: string): number | null {
    const value = record[key];
    return typeof value === 'number' ? value : null;
}

/**
 * CHECKOUT-10 — Delivery Groups (legacy-parity layout).
 *
 * Renders the delivery shipment's productItems bucketed into three sections in
 * a fixed order: HD (Ashley Home Delivery), DS (Standard Delivery), DS3PL
 * (White Glove / 3PBB). Empty sections are skipped. BOPIS items (different
 * `shipmentId`) are ignored.
 *
 * Each section header is a heading + count chip ("1 PRODUCT" for HD, "2
 * DELIVERIES" for DS / DS3PL — matches legacy `cart-primary.isml`).
 *
 * Each line item carries its own "DELIVERY OPTIONS" footer banner with the
 * selected method:
 *   • HD     — embeds <DeliveryMethodToggle variant="inline" />, which renders
 *              either the HD/THD radio group (when `c_thresholdEligible === true`)
 *              or a read-only White Glove row (when not eligible).
 *   • DS     — read-only "Ground Shipping" row with the OOTB shipping-method
 *              price (Free when ≤ 0).
 *   • DS3PL  — read-only "White Glove Delivery" row with `c_whiteGloveServiceFee`
 *              × this line's quantity. Legacy parity — see
 *              `org_ashcomm/deliverycardoption.isml:32-38`, which selects the
 *              White Glove content asset for 3PBB items when
 *              `whiteGloveServiceFeeEnabled` + `whiteGloveServiceFee > 0`.
 */
export default function DeliveryGroups({
    className,
    productMapPromise,
    promotionsPromise,
    toggleUseActions,
    useActions = useCheckoutActions,
    isCompleted,
    isEditing,
    onEdit,
    shippingMethodDetailsContentAssets,
}: DeliveryGroupsProps = {}) {
    const { t, i18n } = useTranslation('checkout');
    const basket = useBasket();
    const currency = useCurrency();
    const config = useConfig<AppConfig>();
    const productMap = productMapPromise ? use(productMapPromise) : undefined;
    const promotions = promotionsPromise ? use(promotionsPromise) : undefined;
    const { confirmDeliveryOptions, hdShippingTypeFetcher } = useActions();
    /*
     * Modal open + content state are intentionally decoupled.
     *
     * `detailsState` carries the (variant, methodName) selected by the user when they clicked a
     * "Delivery Details" / "Free Details" CTA. `isDetailsOpen` controls Radix Dialog visibility
     * independently. On close we only flip `isDetailsOpen` to `false`; we do NOT clear
     * `detailsState`. This keeps the modal body stable for the duration of Radix's exit animation
     * (Radix keeps the content mounted while it fades out). If we cleared the state synchronously,
     * the modal would re-render with `variant=undefined, methodName=undefined`, the content asset
     * would unmount, and the dialog body would flicker visibly during the close animation.
     * The next open overwrites `detailsState` cleanly — no need to reset between opens.
     */
    const [detailsState, setDetailsState] = useState<{
        variant: 'delivery' | 'free';
        methodName?: string;
        deliveryMethod?: DeliveryMethod;
    } | null>(null);
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    const openDetails = (next: {
        variant: 'delivery' | 'free';
        methodName?: string;
        deliveryMethod?: DeliveryMethod;
    }) => {
        setDetailsState(next);
        setIsDetailsOpen(true);
    };
    const [isHdSignatureAcknowledged, setIsHdSignatureAcknowledged] = useState(false);
    const [showHdSignatureError, setShowHdSignatureError] = useState(false);
    const [isSignatureStateHydrated, setIsSignatureStateHydrated] = useState(false);
    const [expandedKitIncludes, setExpandedKitIncludes] = useState<Record<string, boolean>>({});
    const signatureStorageKey = basket?.basketId ? `checkout:hd-signature-ack:${basket.basketId}` : null;

    useEffect(() => {
        if (!signatureStorageKey || typeof window === 'undefined') {
            setIsSignatureStateHydrated(false);
            return;
        }
        const storedValue = window.sessionStorage.getItem(signatureStorageKey);
        if (storedValue !== null) {
            setIsHdSignatureAcknowledged(storedValue === 'true');
            setShowHdSignatureError(false);
        }
        setIsSignatureStateHydrated(true);
    }, [signatureStorageKey]);

    useEffect(() => {
        if (!signatureStorageKey || typeof window === 'undefined' || !isSignatureStateHydrated) {
            return;
        }
        window.sessionStorage.setItem(signatureStorageKey, String(isHdSignatureAcknowledged));
    }, [signatureStorageKey, isHdSignatureAcknowledged, isSignatureStateHydrated]);

    const deliveryShipment = getDeliveryShipment(basket);
    if (!deliveryShipment || !basket) {
        return null;
    }

    const basketRecord = basket as unknown as Record<string, unknown>;
    // SSF-374 — white-glove is a SINGLE flat fee (legacy parity), applied once
    // per basket, only when the basket contains at least one true 3PBB item.
    // The backend (`ShippingCostHelper.computeShippingComponents` →
    // `BasketEnrichmentHelper` `c_whiteGloveTotal`) is the source of truth —
    // it is the exact amount added to `basket.shippingTotal` for white-glove,
    // so the DS3PL card and the Order Summary read from ONE number and can
    // never drift. Do NOT compute qty × site-pref-fee locally: that path
    // ignores the 3PBB predicate (mis-charges non-3PBB DS3PL items) and
    // wrongly multiplies by quantity.
    const whiteGloveTotal = getNumberAttr(basketRecord, 'c_whiteGloveTotal') ?? 0;
    const currentHdShippingType: 'HD' | 'THD' = basketRecord.c_hdShippingType === 'THD' ? 'THD' : 'HD';
    const isSubmitting = hdShippingTypeFetcher.state === 'submitting';

    // SSF-440 (part A) — strip Extend warranty PLIs before bucketing. Warranty
    // PLIs carry the same `shipmentId` as the merchandise they cover, so
    // without this filter they render as standalone rows in the Delivery
    // Method section (with no image, no delivery date, and the "PROTECTION
    // PLAN" name only). The order-summary side of the page already accounts
    // for their cost via `basket.c_totalPPCost`; the delivery groups list
    // should only ever contain merchandise. Legacy SFRA parity: SFRA never
    // rendered warranty items in the shipping-options block.
    const deliveryItems = (basket.productItems ?? []).filter(
        (item) => item.shipmentId === deliveryShipment.shipmentId && !isProtectionPlanItem(item)
    );

    if (deliveryItems.length === 0) {
        return null;
    }

    // Bucket by c_deliveryMethod (shared with the order-review step — CHECKOUT-14)
    const { buckets, visibleGroups } = bucketItemsByDeliveryMethod(deliveryItems);
    if (visibleGroups.length === 0) {
        return null;
    }
    const hasAshleyHomeDelivery = visibleGroups.includes('HD');
    const kitGroupByItemId = buildKitGroupByItemId(getKitGroups(basket as KitBasketFields | undefined));
    const findKitMasterProduct = (group: KitGroup) => {
        if (!group.masterId) return undefined;
        return (
            productMap?.[group.masterId] ||
            Object.values(productMap ?? {}).find(
                (product) => product.id === group.masterId || product.master?.masterId === group.masterId
            )
        );
    };

    const resolveKitVisualItem = (row: Extract<DeliveryRow, { kind: 'kit' }>): EnrichedProductItem | undefined => {
        const componentItem = row.componentItems[0];
        const componentProduct = componentItem?.itemId ? productMap?.[componentItem.itemId] : undefined;
        const masterProduct = findKitMasterProduct(row.group);

        if (masterProduct) return masterProduct as EnrichedProductItem;
        if (componentProduct && componentItem) {
            return {
                ...componentProduct,
                ...componentItem,
            } as EnrichedProductItem;
        }
        return componentItem as EnrichedProductItem | undefined;
    };

    const buildDeliveryRows = (items: ProductItem[]): DeliveryRow[] => {
        const rows: DeliveryRow[] = [];
        const renderedKitNumbers = new Set<string>();

        for (const item of items) {
            const group = item.itemId ? kitGroupByItemId.get(item.itemId) : undefined;
            if (group) {
                if (renderedKitNumbers.has(group.kitNumber)) continue;
                renderedKitNumbers.add(group.kitNumber);
                rows.push({
                    kind: 'kit',
                    group,
                    componentItems: items.filter((candidate) =>
                        Boolean(candidate.itemId && group.itemIds.includes(candidate.itemId))
                    ),
                });
                continue;
            }

            // Defensive fallback: if an orphaned exploded component appears before kitGroups
            // is refreshed, never show it as a standalone row with its own price.
            if ((item as { c_partOfKitNumber?: string | null }).c_partOfKitNumber) continue;
            rows.push({ kind: 'item', item });
        }

        return rows;
    };

    /*
     * "Value of Eligible Items in Your Cart" — passed to `<ShippingMethodDetailsModal/>` so the
     * fee-details content assets (White Glove / Doorstep) can substitute the
     * `${eligibleItemsTotal}` placeholder authored in their BM body.
     *
     * Both fee tables are driven by the value of HD-shipped items: White Glove serves the
     * threshold-eligible HD subset, Doorstep serves all HD items. The OOTB legacy site used a
     * single hard-coded $4,387.99 example for both modals, so summing the entire HD bucket here
     * matches legacy parity. If product wants a stricter "only `c_thresholdEligible` items
     * count toward White Glove" rule later, gate the sum on that custom attribute.
     *
     * Sum uses `priceAfterItemDiscount * quantity` when available (so promo-discounted items
     * contribute their discounted line total), falling back to unit `price * quantity`. SCAPI's
     * `priceAfterItemDiscount` is the unit price after item-level discount, not the line total —
     * we still multiply by quantity. NaN-guard: any malformed PLI contributes 0.
     */
    const hdBucketSubtotal = (buckets.HD ?? []).reduce((sum, item) => {
        const unit =
            typeof item.priceAfterItemDiscount === 'number' && !Number.isNaN(item.priceAfterItemDiscount)
                ? item.priceAfterItemDiscount
                : typeof item.price === 'number' && !Number.isNaN(item.price)
                  ? item.price
                  : 0;
        const qty = typeof item.quantity === 'number' && !Number.isNaN(item.quantity) ? item.quantity : 0;
        return sum + unit * qty;
    }, 0);
    /*
     * `amount` powers the runtime row-highlight in the fee-details modal (the modal walks the
     * BM-authored fee-table rows for one whose `data-fee-min/data-fee-max` range covers this
     * value and applies the green active-state style to it). `formatted` is substituted into
     * the `${eligibleItemsTotal}` placeholder in the BM body. Both must reflect the same cart
     * subtotal — bundle them in a single object to keep that invariant typed at the call-site.
     */
    const eligibleItemsTotal = {
        amount: hdBucketSubtotal,
        formatted: formatCurrency(hdBucketSubtotal, i18n.language, currency),
    };

    /**
     * Section header count chip.
     *  • HD       → "{count} PRODUCT(S)" — legacy treats the HD section as the
     *               hero "Ashley Home Delivery" shipment and counts products.
     *  • DS/DS3PL → "{count} DELIVERY/DELIVERIES" — each line is its own
     *               separate ship-from-vendor delivery in the legacy site.
     */
    const renderHeaderCount = (group: DeliveryMethod, count: number): string => {
        if (group === 'HD') {
            return t('shipping.headerCount.product', { count });
        }
        return t('shipping.headerCount.delivery', { count });
    };

    /**
     * For DS / DS3PL we render a non-interactive selected method row below all
     * line items in the group. Returns `{ name, description, price }`. `price`
     * is `null` when no fee can be resolved — the row renders without a price
     * chip on the right, matching legacy ds-shipping-cards.isml (which simply
     * omits the price element rather than showing a placeholder).
     *
     * DS3PL price is the single basket-level `c_whiteGloveTotal` computed by
     * the backend (`ShippingCostHelper.computeShippingComponents`) — the exact
     * amount added to `basket.shippingTotal` for white-glove. This is the ONLY
     * source of truth so the card price and the Order Summary shipping line
     * can never drift (SSF-374). When zero (non-3PBB DS3PL items — items
     * bucketed as DS3PL solely by `directShipThirdPartyFlag`), the DS3PL card
     * renders "White Glove Delivery" WITHOUT a price chip, matching legacy
     * parity (`CartUtils.returnWhiteGloveServiceFeeForTheBasket` returns
     * `undefined` when no 3PBB item is present).
     */
    const getStandardMethodForGroup = (
        group: 'DS' | 'DS3PL',
        _items: ProductItem[]
    ): { name: string; description: string; price: string | null } => {
        if (group === 'DS') {
            const methodPrice = deliveryShipment.shippingMethod?.price;
            const priceLabel =
                typeof methodPrice === 'number'
                    ? methodPrice <= 0
                        ? t('shipping.free')
                        : formatCurrency(methodPrice, i18n.language, currency)
                    : null;
            return {
                name: t('shipping.method.groundShipping'),
                description: t('shipping.method.groundShippingDescription'),
                price: priceLabel,
            };
        }
        // DS3PL — single flat white-glove fee from the backend (`c_whiteGloveTotal`).
        // Labelled "White Glove Delivery" to match legacy parity
        // (org_ashcomm/deliverycardoption.isml:32-38 — 3PBB items render the
        // `delivery-msg-cart-whiteGlove-*` asset).
        return {
            name: t('shipping.method.whiteGlove'),
            description: t('shipping.method.whiteGloveDescription'),
            price: whiteGloveTotal > 0 ? formatCurrency(whiteGloveTotal, i18n.language, currency) : null,
        };
    };

    // ToggleCard wrapping is opt-in via `isEditing` (and friends). When the
    // host page provides step state we render the OOTB collapse pattern so
    // the section auto-collapses to a one-line summary after the shopper
    // hits "Continue to Payment". When step state is omitted (Storybook /
    // unit tests that pre-date this prop), we render flat.
    const useToggleCard = typeof isEditing === 'boolean';
    const editing = isEditing ?? true;

    // Summary line shown when collapsed — mirrors OOTB
    // `shippingOptions.priceAndMethod`: "{{price}} | {{methodName}}".
    const hdFee = getNumberAttr(basketRecord, 'c_hdFee');
    const thdFee = getNumberAttr(basketRecord, 'c_thdFee');
    const summaryFee = currentHdShippingType === 'THD' ? thdFee : hdFee;
    const summaryMethodName =
        currentHdShippingType === 'THD' ? t('shipping.method.doorstep') : t('shipping.method.whiteGlove');
    const summaryPriceLabel =
        summaryFee === null
            ? t('shipping.method.feeCalculating')
            : summaryFee <= 0
              ? t('shipping.free')
              : formatCurrency(summaryFee, i18n.language, currency);

    const groupsBody = (
        <div className="flex flex-col gap-5">
            {/* Reduce-deliveries promo banner — top of the Delivery Method section (Figma node 34613-66829). */}
            <FewerDeliveriesToggle />
            {visibleGroups.map((group) => {
                const items = buckets[group];
                const deliveryRows = buildDeliveryRows(items);
                const headingId = `delivery-group-${group.toLowerCase()}-heading`;
                const standardMethod =
                    group === 'DS' || group === 'DS3PL' ? getStandardMethodForGroup(group, items) : null;
                /*
                 * Per-group delivery date. HD reads the shopper-selectable
                 * shipment-level `c_requestedDeliveryDatetime`. DS / DS3PL
                 * read the fulfiller-quoted per-item ATP `c_atpBestDate` so
                 * changing the White Glove calendar can never move the
                 * ground-shipping / 3PBB date.
                 */
                const standardDeliveryDate =
                    group === 'DS' || group === 'DS3PL'
                        ? formatDeliveryDate(getGroupDeliveryDate(group, items, deliveryShipment), i18n.language)
                        : null;
                return (
                    <section
                        key={group}
                        role="group"
                        aria-labelledby={headingId}
                        data-testid={`delivery-group-${group.toLowerCase()}`}>
                        {/* Section header — heading + count chip (legacy `.hd-title` + `.hd-item-count`) */}
                        <header className="my-4 flex items-center gap-2">
                            <h3 id={headingId} className="text-xl font-publico-headline leading-7">
                                {t(GROUP_LABEL_KEYS[group])}
                            </h3>
                            <span
                                className="text-xs font-medium uppercase tracking-button text-muted-foreground"
                                data-testid={`delivery-group-${group.toLowerCase()}-count`}>
                                {renderHeaderCount(group, deliveryRows.length)}
                            </span>
                        </header>

                        <ul className="flex flex-col divide-y divide-border-border border-t border-border">
                            {deliveryRows.map((row, idx) => {
                                if (row.kind === 'item') {
                                    const item = row.item;
                                    const productName = item.productName || 'Product';
                                    const fulfillerName = getStringAttr(item, 'c_fulfillerDisplayName');
                                    const { src: imageUrl, alt: imageAlt } = resolveProductItemImage(
                                        item,
                                        productMap,
                                        config
                                    );
                                    const quantity = item.quantity ?? 1;
                                    const productData = item.itemId ? productMap?.[item.itemId] : undefined;
                                    const displayVariationValues = getDisplayVariationValues(
                                        (item.variationAttributes as ShopperProducts.schemas['VariationAttribute'][]) ??
                                            productData?.variationAttributes ??
                                            [],
                                        (item.variationValues as Record<string, string> | undefined) ??
                                            productData?.variationValues ??
                                            {}
                                    );
                                    const variationEntries = Object.entries(displayVariationValues);
                                    const mergedForPricing = productData
                                        ? ({
                                              ...productData,
                                              ...item,
                                          } as ShopperBasketsV2.schemas['ProductItem'] &
                                              Partial<ShopperProducts.schemas['Product']>)
                                        : item;

                                    return (
                                        <li
                                            key={item.itemId ?? `${group}-${idx}`}
                                            data-testid={`delivery-group-${group.toLowerCase()}-item-${item.productId ?? idx}`}>
                                            <div className="flex items-start gap-3 py-5">
                                                <div className="relative size-16 shrink-0 overflow-hidden rounded-xs bg-product-image">
                                                    {imageUrl ? (
                                                        <DynamicImage
                                                            src={imageUrl}
                                                            alt={imageAlt}
                                                            widths={[64, 128]}
                                                            heights={[64, 128]}
                                                            loading="lazy"
                                                            className="absolute inset-0 size-full object-cover"
                                                            imageProps={{ width: 128, height: 128, decoding: 'async' }}
                                                        />
                                                    ) : null}
                                                </div>
                                                <div className="min-w-0 flex-1 md:flex items-start gap-3 justify-between">
                                                    <div className="flex-1 min-w-0">
                                                        <Typography
                                                            variant="p"
                                                            className="truncate text-sm font-medium leading-tight text-foreground">
                                                            {productName}
                                                        </Typography>
                                                        <Typography
                                                            variant="small"
                                                            className="text-xs text-muted-foreground">
                                                            {t('shipping.itemCount', { count: quantity })}
                                                        </Typography>
                                                        {item.productId && (
                                                            <Typography
                                                                variant="small"
                                                                className="text-xs text-muted-foreground mt-0.5"
                                                                data-testid={`delivery-group-${group.toLowerCase()}-item-${item.productId}-product-id`}>
                                                                {t('shipping.itemLabel', { productId: item.productId })}
                                                            </Typography>
                                                        )}
                                                        {variationEntries.length > 0 && (
                                                            <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground mt-0.5">
                                                                {variationEntries.map(([name, value], index) => (
                                                                    <div
                                                                        key={name}
                                                                        className="inline-flex items-center gap-1.5">
                                                                        <span>{value}</span>
                                                                        {index < variationEntries.length - 1 ? (
                                                                            <span aria-hidden="true">•</span>
                                                                        ) : null}
                                                                    </div>
                                                                ))}
                                                                <span className="sr-only">
                                                                    {variationEntries
                                                                        .map(([name, value]) => `${name}: ${value}`)
                                                                        .join(', ')}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {fulfillerName && (
                                                            <Typography
                                                                variant="small"
                                                                className="mt-0.5 text-xs text-muted-foreground"
                                                                data-testid={`delivery-group-${group.toLowerCase()}-fulfilledby-${item.productId ?? idx}`}>
                                                                {t('shipping.fulfilledBy', { source: fulfillerName })}
                                                            </Typography>
                                                        )}
                                                    </div>
                                                    <div className="shrink-0 flex flex-col md:items-end gap-0.5">
                                                        <ProductPrice
                                                            product={mergedForPricing}
                                                            currency={currency}
                                                            type="total"
                                                            quantity={quantity}
                                                            hidePromo
                                                            hasDiscount={!!item.priceAdjustments?.length}
                                                            className="flex items-baseline gap-1.5 text-sm font-medium text-foreground"
                                                            listPriceProps={{ className: 'text-sm font-normal' }}
                                                        />
                                                        <CartLinePromoCallouts
                                                            priceAdjustments={item.priceAdjustments}
                                                            promotions={promotions}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </li>
                                    );
                                }

                                const kitTitle = row.group.name || row.componentItems[0]?.productName || 'Product';
                                const quantity = row.group.quantity ?? 1;
                                const kitVisualItem = resolveKitVisualItem(row);
                                const kitMasterProduct = findKitMasterProduct(row.group);
                                const kitPricing = getKitGroupPricing(row.group, row.componentItems);
                                const kitInstances = row.group.quantity > 0 ? row.group.quantity : 1;
                                const includesItems = row.componentItems.map((component) => {
                                    const apkQuantity = (component as { c_apkQuantity?: number }).c_apkQuantity;
                                    const perKit =
                                        apkQuantity ??
                                        (typeof component.quantity === 'number'
                                            ? Math.max(1, Math.round(component.quantity / kitInstances))
                                            : 1);
                                    return {
                                        key:
                                            component.itemId ??
                                            `${row.group.kitNumber}-${component.productId ?? 'component'}`,
                                        label: String(
                                            component.productName || component.name || component.productId || ''
                                        ),
                                        quantity: Number(perKit),
                                    };
                                });
                                const kitKey = row.group.kitNumber;
                                const isIncludesExpanded = expandedKitIncludes[kitKey] ?? true;

                                return (
                                    <li
                                        key={`kit-${row.group.kitNumber}`}
                                        data-testid={`delivery-group-${group.toLowerCase()}-kit-${row.group.masterId ?? row.group.kitNumber}`}>
                                        <div className="flex items-start gap-3 py-5">
                                            {kitVisualItem ? (
                                                <KitGroupImage
                                                    productItem={kitVisualItem}
                                                    title={kitTitle}
                                                    widths={[64, 128]}
                                                    containerClassName="relative size-16 shrink-0 overflow-hidden rounded-xs"
                                                />
                                            ) : (
                                                <div className="relative size-16 shrink-0 overflow-hidden rounded-xs bg-muted" />
                                            )}
                                            <div className="min-w-0 flex-1 md:flex items-start gap-3 justify-between">
                                                <div className="flex-1 min-w-0">
                                                    <Typography
                                                        variant="p"
                                                        className="truncate text-sm font-medium leading-tight text-foreground">
                                                        {kitTitle}
                                                    </Typography>
                                                    <Typography
                                                        variant="small"
                                                        className="text-xs text-muted-foreground">
                                                        {t('shipping.itemCount', { count: quantity })}
                                                    </Typography>
                                                    {/* Item ID — mirrors the standard non-kit row
                                                        below (`t('shipping.itemLabel', { productId })`)
                                                        so kit rows on the Delivery step surface a
                                                        comparable identifier. Anchored on the kit
                                                        master id with a fallback to the first
                                                        component's `productId` for the (unlikely)
                                                        stale-basket case where the master id is
                                                        missing. */}
                                                    {(row.group.masterId ?? row.componentItems[0]?.productId) ? (
                                                        <Typography
                                                            variant="small"
                                                            className="text-xs text-muted-foreground mt-0.5"
                                                            data-testid={`delivery-group-${group.toLowerCase()}-kit-${row.group.masterId ?? row.group.kitNumber}-product-id`}>
                                                            {t('shipping.itemLabel', {
                                                                productId:
                                                                    row.group.masterId ??
                                                                    row.componentItems[0]?.productId,
                                                            })}
                                                        </Typography>
                                                    ) : null}
                                                    <KitItemVariations
                                                        items={row.componentItems.map((component) => {
                                                            const mappedProduct = component.itemId
                                                                ? productMap?.[component.itemId]
                                                                : undefined;
                                                            return mappedProduct
                                                                ? ({
                                                                      ...mappedProduct,
                                                                      ...component,
                                                                  } as EnrichedProductItem)
                                                                : (component as EnrichedProductItem);
                                                        })}
                                                        masterProduct={kitMasterProduct}
                                                        kitTitle={kitTitle}
                                                    />
                                                    {includesItems.length > 0 ? (
                                                        <div
                                                            className="mt-3 space-y-2"
                                                            data-testid={`delivery-kit-includes-${kitKey}`}>
                                                            <button
                                                                type="button"
                                                                className="flex w-full items-center justify-between text-sm font-medium text-foreground"
                                                                aria-expanded={isIncludesExpanded}
                                                                onClick={() =>
                                                                    setExpandedKitIncludes((prev) => ({
                                                                        ...prev,
                                                                        [kitKey]: !isIncludesExpanded,
                                                                    }))
                                                                }>
                                                                <span>Product Includes:</span>
                                                                {isIncludesExpanded ? (
                                                                    <ChevronUp
                                                                        className="h-4 w-4 shrink-0"
                                                                        aria-hidden
                                                                    />
                                                                ) : (
                                                                    <ChevronDown
                                                                        className="h-4 w-4 shrink-0"
                                                                        aria-hidden
                                                                    />
                                                                )}
                                                            </button>
                                                            {isIncludesExpanded ? (
                                                                <div className="space-y-2 pl-2">
                                                                    {includesItems.map((component) => (
                                                                        <div
                                                                            key={component.key}
                                                                            className="space-y-0.5">
                                                                            <div className="text-sm text-foreground">
                                                                                {component.label}
                                                                            </div>
                                                                            <div className="text-sm text-muted-foreground">
                                                                                Qty: {component.quantity}
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            ) : null}
                                                        </div>
                                                    ) : null}
                                                </div>
                                                <div className="shrink-0 flex flex-col md:items-end gap-0.5">
                                                    {kitPricing.hasSavings ? (
                                                        <span className="text-sm font-normal text-muted-foreground line-through">
                                                            {formatCurrency(
                                                                kitPricing.originalLineTotal,
                                                                i18n.language,
                                                                currency
                                                            )}
                                                        </span>
                                                    ) : null}
                                                    <span className="text-sm font-medium text-foreground">
                                                        {formatCurrency(row.group.lineTotal, i18n.language, currency)}
                                                    </span>
                                                    <CartLinePromoCallouts
                                                        priceAdjustments={kitPricing.promoAdjustments}
                                                        promotions={promotions}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>

                        {/*
                          Delivery-method selection — rendered once for the entire group,
                          below the product list. HD renders the inline toggle (radio when
                          threshold-eligible, read-only otherwise); DS / DS3PL render a
                          non-interactive selected method row with the aggregate group price.
                        */}
                        <div className="pt-4" data-testid={`delivery-group-${group.toLowerCase()}-options`}>
                            {group === 'HD' ? (
                                <DeliveryMethodToggle
                                    variant="inline"
                                    useActions={toggleUseActions}
                                    onDeliveryDetails={(methodName) =>
                                        openDetails({
                                            variant: 'delivery',
                                            methodName,
                                            deliveryMethod: 'HD',
                                        })
                                    }
                                    onFreeDetails={(methodName) =>
                                        openDetails({
                                            variant: 'free',
                                            methodName,
                                            deliveryMethod: 'HD',
                                        })
                                    }
                                />
                            ) : standardMethod ? (
                                <RadioDeliveryMethodTile
                                    control="none"
                                    status="info"
                                    title={standardMethod.name}
                                    price={standardMethod.price}
                                    description={standardMethod.description}
                                    deliveryDate={standardDeliveryDate}
                                    onDeliveryDetails={() =>
                                        openDetails({
                                            variant: group === 'DS' ? 'free' : 'delivery',
                                            methodName: group === 'DS' ? undefined : standardMethod.name,
                                            deliveryMethod: group,
                                        })
                                    }
                                    showDeliveryDetails={true}
                                    // Third-party White Glove (DS3PL) is a flat per-item fee — no
                                    // value-range fee table — so we don't surface a "Fee details"
                                    // CTA on the DS3PL tile. DS (Ground Shipping) never had one.
                                    showFreeDetails={false}
                                    priceTestId={`delivery-group-${group.toLowerCase()}-method-price`}
                                    data-testid={`delivery-group-${group.toLowerCase()}-method-tile`}
                                />
                            ) : null}
                        </div>
                    </section>
                );
            })}
        </div>
    );

    /*
     * Continue to Payment — confirms the shipping-options step. Re-PATCHes
     * the current `c_hdShippingType` (idempotent; also flips
     * `c_shippingMethodConfirmed = true` server-side) and advances the
     * checkout step state machine via `confirmDeliveryOptions`.
     */
    const continueButton = (
        <div className="mt-6 w-full">
            {hasAshleyHomeDelivery ? (
                <div className="mb-4 py-3">
                    <p className="text-sm font-semibold text-foreground">{t('shipping.signatureRequired.heading')}</p>
                    {/*
                     * Checkbox + label live in their own flex row so the checkbox stays vertically
                     * centered with the label text regardless of whether the error below is shown.
                     * Previously the error was a sibling of the label inside the right column, which
                     * with `items-center` pulled the checkbox down to the visual centre of the
                     * label-and-error stack — breaking alignment with the label as soon as the
                     * error appeared. The error now sits below the row, indented `pl-6` (size-4
                     * checkbox + gap-2 = 24px) so it lines up with the label text.
                     */}
                    <div className="mt-2">
                        <div className="flex items-start gap-2 md:items-center">
                            <Checkbox
                                id="delivery-signature-ack"
                                checked={isHdSignatureAcknowledged}
                                onCheckedChange={(checked) => {
                                    const nextChecked = checked === true;
                                    setIsHdSignatureAcknowledged(nextChecked);
                                    if (nextChecked) {
                                        setShowHdSignatureError(false);
                                    }
                                }}
                                aria-invalid={showHdSignatureError}
                                className="mt-1 md:mt-0"
                                data-testid="delivery-signature-ack-checkbox"
                            />
                            <Label htmlFor="delivery-signature-ack" className="text-base font-normal">
                                {t('shipping.signatureRequired.checkboxLabel')}
                            </Label>
                        </div>
                        {showHdSignatureError ? (
                            <p
                                className="mt-1 pl-6 text-base text-destructive"
                                role="alert"
                                data-testid="delivery-signature-ack-error">
                                {t('shipping.signatureRequired.error')}
                            </p>
                        ) : null}
                    </div>
                </div>
            ) : null}
            <Button
                type="button"
                onClick={() => {
                    if (hasAshleyHomeDelivery && !isHdSignatureAcknowledged) {
                        setShowHdSignatureError(true);
                        return;
                    }
                    confirmDeliveryOptions(currentHdShippingType);
                }}
                disabled={isSubmitting}
                className="h-auto whitespace-normal w-full lg:w-fit"
                data-testid="delivery-groups-continue">
                {isSubmitting ? t('shipping.saving') : t('shipping.continue')}
            </Button>
        </div>
    );

    if (!useToggleCard) {
        // Backwards-compat: stories / unit tests that pre-date the
        // ToggleCard wrapping render flat with the Continue button inline.
        return (
            <>
                <div className={className} data-testid="delivery-groups">
                    {groupsBody}
                    {continueButton}
                </div>
                <ShippingMethodDetailsModal
                    open={isDetailsOpen}
                    variant={detailsState?.variant}
                    methodName={detailsState?.methodName}
                    deliveryMethod={detailsState?.deliveryMethod}
                    onOpenChange={setIsDetailsOpen}
                    contentAssets={shippingMethodDetailsContentAssets}
                    eligibleItemsTotal={eligibleItemsTotal}
                />
            </>
        );
    }

    return (
        <>
            <ToggleCard
                id="delivery-groups"
                title={<>{t('shipping.heading')}</>}
                editing={editing}
                disabled={false}
                onEdit={onEdit}
                editLabel={t('common.edit')}
                disableEdit={!isCompleted}
                isLoading={isSubmitting}>
                <ToggleCardEdit>
                    <div className={className} data-testid="delivery-groups">
                        {groupsBody}
                        {continueButton}
                    </div>
                </ToggleCardEdit>

                <ToggleCardSummary>
                    <div className="flex flex-col gap-8" data-testid="delivery-groups-summary">
                        {visibleGroups.map((group) => {
                            const summaryItems = buckets[group];
                            const summaryRows = buildDeliveryRows(summaryItems);
                            const standardMethod =
                                group === 'DS' || group === 'DS3PL'
                                    ? getStandardMethodForGroup(group, summaryItems)
                                    : null;
                            const methodName = group === 'HD' ? summaryMethodName : standardMethod?.name;
                            const methodPrice = group === 'HD' ? summaryPriceLabel : standardMethod?.price;
                            // Per-group date — HD from shopper-selected shipment date, DS/DS3PL from ATP.
                            const summaryDeliveryDate = formatDeliveryDate(
                                getGroupDeliveryDate(group, summaryItems, deliveryShipment),
                                i18n.language
                            );

                            return (
                                <div key={group}>
                                    <p className="mb-3 text-sm text-foreground">
                                        {summaryDeliveryDate && (
                                            <>
                                                <span className="font-bold">
                                                    {t('confirmation.summaryLabels.arriving')} {summaryDeliveryDate}
                                                </span>
                                                <span aria-hidden="true"> · </span>
                                            </>
                                        )}
                                        {methodName && (
                                            <>
                                                <span>{methodName}</span>
                                                <span aria-hidden="true"> · </span>
                                            </>
                                        )}
                                        {methodPrice && (
                                            <>
                                                <span>{methodPrice}</span>
                                            </>
                                        )}
                                    </p>
                                    <div className="divide-y divide-border overflow-hidden rounded-md border border-border p-3">
                                        {summaryRows.map((row, idx) => {
                                            const item = row.kind === 'kit' ? row.componentItems[0] : row.item;
                                            const { src: imageUrl, alt: imageAlt } =
                                                row.kind === 'kit'
                                                    ? { src: undefined, alt: '' }
                                                    : resolveProductItemImage(item, productMap, config);
                                            const kitVisualItem =
                                                row.kind === 'kit' ? resolveKitVisualItem(row) : undefined;
                                            const summaryProductData = item?.itemId
                                                ? productMap?.[item.itemId]
                                                : undefined;
                                            const summaryVariationValues =
                                                row.kind === 'item' && item
                                                    ? getDisplayVariationValues(
                                                          (item.variationAttributes as ShopperProducts.schemas['VariationAttribute'][]) ??
                                                              summaryProductData?.variationAttributes ??
                                                              [],
                                                          (item.variationValues as
                                                              | Record<string, string>
                                                              | undefined) ??
                                                              summaryProductData?.variationValues ??
                                                              {}
                                                      )
                                                    : {};
                                            const summaryVariationEntries = Object.entries(summaryVariationValues);
                                            const title =
                                                row.kind === 'kit'
                                                    ? row.group.name || row.componentItems[0]?.productName || 'Product'
                                                    : (row.item.productName ?? 'Product');
                                            const subtitle =
                                                row.kind === 'kit'
                                                    ? `${t('shipping.itemCount', { count: row.group.quantity ?? 1 })} · Includes ${row.componentItems.length} ${
                                                          row.componentItems.length === 1 ? 'item' : 'items'
                                                      }`
                                                    : null;

                                            return (
                                                <div
                                                    key={
                                                        row.kind === 'kit'
                                                            ? `summary-kit-${row.group.kitNumber}`
                                                            : (row.item.itemId ?? `summary-${group}-${idx}`)
                                                    }
                                                    className="flex items-center gap-3 pb-3 last:pb-0 mb-3 last:mb-0">
                                                    {row.kind === 'kit' && kitVisualItem ? (
                                                        <KitGroupImage
                                                            productItem={kitVisualItem}
                                                            title={title}
                                                            widths={[48, 96]}
                                                            containerClassName="relative size-12 shrink-0 overflow-hidden rounded-sm"
                                                        />
                                                    ) : (
                                                        <div className="relative size-12 shrink-0 overflow-hidden rounded-sm bg-muted">
                                                            {imageUrl && (
                                                                <DynamicImage
                                                                    src={imageUrl}
                                                                    alt={imageAlt}
                                                                    widths={[48, 96]}
                                                                    heights={[48, 96]}
                                                                    loading="lazy"
                                                                    className="absolute inset-0 size-full object-cover"
                                                                    imageProps={{
                                                                        width: 96,
                                                                        height: 96,
                                                                        decoding: 'async',
                                                                    }}
                                                                />
                                                            )}
                                                        </div>
                                                    )}
                                                    <div className="min-w-0">
                                                        <span className="text-sm font-medium text-foreground">
                                                            {title}
                                                        </span>
                                                        {subtitle ? (
                                                            <div className="text-xs text-muted-foreground mt-0.5">
                                                                {subtitle}
                                                            </div>
                                                        ) : null}
                                                        {row.kind === 'kit' ? (
                                                            <KitItemVariations
                                                                items={row.componentItems.map((component) => {
                                                                    const mappedProduct = component.itemId
                                                                        ? productMap?.[component.itemId]
                                                                        : undefined;
                                                                    return mappedProduct
                                                                        ? ({
                                                                              ...mappedProduct,
                                                                              ...component,
                                                                          } as EnrichedProductItem)
                                                                        : (component as EnrichedProductItem);
                                                                })}
                                                                masterProduct={findKitMasterProduct(row.group)}
                                                                kitTitle={title}
                                                            />
                                                        ) : summaryVariationEntries.length > 0 ? (
                                                            <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground mt-0.5">
                                                                {summaryVariationEntries.map(([name, value], index) => (
                                                                    <div
                                                                        key={name}
                                                                        className="inline-flex items-center gap-1.5">
                                                                        <span>{value}</span>
                                                                        {index < summaryVariationEntries.length - 1 ? (
                                                                            <span aria-hidden="true">•</span>
                                                                        ) : null}
                                                                    </div>
                                                                ))}
                                                                <span className="sr-only">
                                                                    {summaryVariationEntries
                                                                        .map(([name, value]) => `${name}: ${value}`)
                                                                        .join(', ')}
                                                                </span>
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </ToggleCardSummary>
            </ToggleCard>
            <ShippingMethodDetailsModal
                open={isDetailsOpen}
                variant={detailsState?.variant}
                methodName={detailsState?.methodName}
                deliveryMethod={detailsState?.deliveryMethod}
                onOpenChange={setIsDetailsOpen}
                contentAssets={shippingMethodDetailsContentAssets}
                eligibleItemsTotal={eligibleItemsTotal}
            />
        </>
    );
}
